import { useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { ShareNetwork, DownloadSimple, SpinnerGap, LinkSimple } from "@phosphor-icons/react";

async function fetchImage(imagePath, authed) {
  const res = await fetch(`${API}${imagePath}`, authed ? { credentials: "include" } : {});
  if (!res.ok) throw new Error("image");
  return res.blob();
}

export function ShareResults({ imagePath, shareUrl, title, authed = false, brand = "#1F6FE5" }) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const blob = await fetchImage(imagePath, authed);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${title.replace(/\s+/g, "_")}_results.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Image downloaded");
    } catch { toast.error("Could not generate image"); }
    finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await fetchImage(imagePath, authed);
      const file = new File([blob], `${title.replace(/\s+/g, "_")}_results.png`, { type: "image/png" });
      const payload = { title: `${title} — Results`, text: `Results for ${title}`, url: shareUrl };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...payload, files: [file] });
      } else if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Results link copied to clipboard");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        try { await navigator.clipboard.writeText(shareUrl); toast.success("Results link copied"); }
        catch { toast.error("Sharing not supported on this device"); }
      }
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Results link copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Results link copied");
      } catch {
        toast.error("Copy not supported — long-press the link URL to copy it.");
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button data-testid="share-results-btn" onClick={share} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 text-white rounded-md text-sm font-semibold transition-colors active:scale-95 disabled:opacity-60" style={{ background: brand }}>
        {busy ? <SpinnerGap size={16} className="animate-spin" /> : <ShareNetwork size={16} weight="fill" />} Share results
      </button>
      <button data-testid="download-image-btn" onClick={download} disabled={busy} className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors" title="Download image">
        <DownloadSimple size={16} />
      </button>
      <button data-testid="copy-link-btn" onClick={copyLink} className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors" title="Copy link">
        <LinkSimple size={16} />
      </button>
    </div>
  );
}
